import {
  Box,
  Container,
  Text,
  Button,
  Divider,
  Flex,
  HStack,
  UnorderedList,
  ListItem,
  VStack,
  useToast,
  Heading,
} from "@chakra-ui/react";
import Link from "next/link";
import { useRouter } from "next/router";
import Head from "next/head";
import Image from "next/image";
import { useEffect, useState } from "react";
import ArticleSidebar from "../../src/articles/ArticleSidebar";
import { postData, deleteData } from "../../src/utilities/axios";
import * as N from "../../src/utilities/notifications";
import {
  useStoryblokState,
  getStoryblokApi,
  StoryblokComponent,
} from "@storyblok/react";
import {
  EmailIcon,
  EmailShareButton,
  FacebookIcon,
  FacebookShareButton,
  LinkedinIcon,
  LinkedinShareButton,
  TwitterShareButton,
  XIcon,
} from "react-share";
import {
  IconTag,
  IconChevronRight,
  IconBookmark,
  IconBookmarkFilled,
} from "../../styles/icons";
import TitleBlok from "../../src/components/storyblok/TitleBlok";
import ArticleAuthorBlurb from "../../src/articles/ArticleAuthorBlurb";
import dayjs from "dayjs";
import TextBlok from "../../src/components/storyblok/TextBlok";
import { useDispatch, useSelector } from "react-redux";
import { updateArticleBookmarks } from "../../src/redux/actions/bookmarks";
import BookingRequestModal from "../../src/booking/initiate-booking/BookingRequestModal";
import LogInModal from "../../src/authentication/LogInModal";
import {
  imageServiceTransformer,
  serializeJsonLd,
} from "../../src/utilities/helpers";
import ArticlePreviewBox from "../../src/articles/ArticlePreviewBox";
import ArticleNewsletterModal, {
  hasNewsletterSubscription,
  hasSeenNewsletterThisSession,
  markNewsletterSeenThisSession,
} from "../../src/articles/ArticleNewsletterModal";
import { useMediaQuery } from "react-responsive";

export async function getStaticProps({ params, draftMode }) {
  const slug = (await params.slug) ? params.slug.join("/") : "home";
  /**
   * @type {import("@storyblok/react").ISbStoriesParams}
   */
  let sbParams = {
    version: draftMode ? "draft" : "published",
    resolve_relations: ["article.authors", "article.readFurther"],
  };
  const storyblokApi = getStoryblokApi();
  let data = {};
  try {
    const response = await storyblokApi.get(
      `cdn/stories/article/${slug}`,
      sbParams,
      {
        cache: draftMode ? "no-cache" : undefined,
        next: {
          revalidate: draftMode ? undefined : 3600,
        },
      },
    );
    data = response.data;
  } catch (e) {
    console.error(`Cannot retrieve single article ${slug}`);
    console.error(e);
    return {
      notFound: true,
    };
  }

  const articleTagMap = {};
  try {
    const datasourceEntries = await storyblokApi.getAll(
      `cdn/datasource_entries`,
      {
        datasource: "articletagmap",
      },
    );
    datasourceEntries.forEach((tag) => {
      articleTagMap[tag.name] = tag.value;
    });
  } catch (e) {
    console.error(e);
    return {
      notFound: true,
    };
  }

  return {
    props: {
      draftMode: draftMode || null,
      story: data?.story ?? false,
      story_id: data?.story?.id ?? false,
      tagMap: articleTagMap,
    },
    revalidate: draftMode ? undefined : 3600,
  };
}

export async function getStaticPaths() {
  const storyblokApi = getStoryblokApi();
  try {
    var articles = await storyblokApi.getAll("cdn/links", {
      version: "published",
      starts_with: "article",
      cv: "undefined",
    });
  } catch (e) {
    console.error(e);
    return {
      paths: [],
      fallback: "blocking",
    };
  }

  const paths = [];
  articles.forEach(({ is_folder, slug }) => {
    if (is_folder || slug === "home") {
      return;
    }

    paths.push({
      params: { slug: slug.replace(/^\/?article\//, "").split("/") },
    });
  });

  return {
    paths: paths,
    fallback: "blocking",
  };
}

export default function Article({ story: initialStory, tagMap, draftMode }) {
  const router = useRouter();
  const dispatch = useDispatch();
  const toast = useToast();
  const story = useStoryblokState(initialStory, {
    resolveRelations: ["article.authors", "article.readFurther"],
  });
  const [content, setContent] = useState(initialStory.content);
  const token = useSelector((state) => state.user.JWTToken);
  const savedArticles = useSelector((state) => state.bookmarks.articles);
  const [selectedPractitioner, setSelectedPractitioner] = useState(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showNewsletterModal, setShowNewsletterModal] = useState(false);
  const [mobileOptimisedHeader, setMobileOptimisedHeader] = useState(null);
  const [optimisedHeader, setOptimisedHeader] = useState(null);
  const isSmallScreen = useMediaQuery({ query: "(max-width: 532px)" });

  useEffect(() => {
    if (story) {
      setContent(story.content);
    }
  }, [story]);

  useEffect(() => {
    setMobileOptimisedHeader(
      imageServiceTransformer(content?.headerImage.filename, "430x344"),
    );
    setOptimisedHeader(
      imageServiceTransformer(
        content?.headerImage.filename,
        "1000x500/filters:quality(50)",
      ),
    );
  }, [content]);

  useEffect(() => {
    if (hasNewsletterSubscription() || hasSeenNewsletterThisSession()) {
      return;
    }
    const timer = window.setTimeout(() => {
      markNewsletterSeenThisSession();
      setShowNewsletterModal(true);
    }, 1000);
    return () => window.clearTimeout(timer);
  }, []);

  const closeNewsletterModal = () => {
    markNewsletterSeenThisSession();
    setShowNewsletterModal(false);
  };

  const addToSavedList = () => {
    try {
      postData(
        token,
        {},
        `${process.env.NEXT_PUBLIC_NEW_USER_URL}articleBookmark/${story.uuid}/add`,
        process.env.NEXT_PUBLIC_NEW_USER_APIKEY,
      ).then(() => {
        dispatch(updateArticleBookmarks([...savedArticles, story.uuid]));
      });
    } catch (error) {
      console.error("ERROR saving bookmark", error);
      toast(N.genericFail("Failed to save bookmark"));
    }
  };

  const removeFromSavedList = () => {
    try {
      postData(
        token,
        {},
        `${process.env.NEXT_PUBLIC_NEW_USER_URL}articleBookmark/${story.uuid}/remove`,
        process.env.NEXT_PUBLIC_NEW_USER_APIKEY,
      ).then(() => {
        dispatch(
          updateArticleBookmarks(
            savedArticles.filter((elem) => elem !== story.uuid),
          ),
        );
      });
    } catch (error) {
      console.error("ERROR removing bookmark", error);
      toast(N.genericFail("Failed to remove bookmark"));
    }
  };

  const handleArticleBookmarkClick = () => {
    if (token !== null) {
      if (savedArticles.includes(story.uuid)) {
        removeFromSavedList();
      } else {
        addToSavedList();
      }
    } else {
      router.push("/user-benefits");
    }
  };

  const articleStructuredData = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: content.title,
    image: content?.headerImage?.filename,
    datePublished: story.published_at,
    dateModified: story.updated_at,
    author: {
      "@type": "Person",
      name: content?.authors[0]?.name,
      url:
        typeof window !== "undefined"
          ? `${window.location.origin}/author/${content?.authors[0]?.slug}`
          : `/counsellors/${content?.authors[0]?.slug}`,
    },
    publisher: {
      "@type": "Organization",
      name: "Welldoing",
      logo: {
        "@type": "ImageObject",
        url: "https://www.welldoing.org/logo.png",
      },
    },
  };

  return (
    <>
      <Head>
        <title>{content.title}</title>
        <meta name="title" content={content.metatags?.title ?? content.title} />
        {content.metatags?.keywords && (
          <meta name="keywords" content={content.metatags.keywords} />
        )}
        <meta property="og:locale" content="en_GB" />
        <meta property="og:type" content="website" />
        <meta
          name="og:title"
          content={content.metatags?.title ?? content.title}
        />
        {content.metatags?.description && (
          <>
            <meta name="description" content={content.metatags?.description} />
            <meta
              name="og:description"
              content={content.metatags.description}
            />
          </>
        )}
        {content.headerImage?.filename && (
          <meta property="og:image" content={content.headerImage?.filename} />
        )}
        <link
          rel="canonical"
          href={`https://welldoing.org/${story.full_slug}`}
        />
      </Head>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: serializeJsonLd(articleStructuredData),
        }}
      />
      {selectedPractitioner !== null && (
        <BookingRequestModal
          show={selectedPractitioner}
          close={() => setSelectedPractitioner(null)}
          practitionerId={selectedPractitioner.userId}
          profileId={selectedPractitioner.profileId}
          practitionerName={selectedPractitioner.fullname}
          practitionerType="therapist"
        />
      )}

      {showLoginModal && (
        <LogInModal
          onCloseModal={() => setShowLoginModal(false)}
          title="Login"
          noRedirect
        />
      )}
      <ArticleNewsletterModal
        isOpen={showNewsletterModal}
        onClose={closeNewsletterModal}
      />
      <Container
        maxW={{ base: "100%", md: "85%" }}
        boxSizing="border-box"
        overflowX="hidden"
        px={{ base: "0px", md: "inherit" }}
        itemScope
        itemType="https://schema.org/BlogPosting"
      >
        <Box width="100%" display="flex">
          <Box mx="auto" my={{ base: "0px", md: "50px" }} width="100%">
            <Flex
              align="center"
              gap="10px"
              display={{ base: "none", md: "flex" }}
            >
              <Link href="/">
                <Text color="brand.400" cursor="pointer">{`Homepage`}</Text>
              </Link>
              <IconChevronRight size="22px" />
              <Link href="/article" legacyBehavior>
                <Text
                  color="brand.400"
                  cursor="pointer"
                >{`Welldoing Articles`}</Text>
              </Link>
              <IconChevronRight size="22px" />
              <Text>{content.title}</Text>
            </Flex>
            <Box my="50px" mt={{ base: "0px", md: "100px" }}>
              {content.headerImage && (
                <div
                  itemProp="image"
                  className="block md:hidden w-full h-[344px] relative z-0 absolute"
                >
                  <Image
                    src={mobileOptimisedHeader}
                    alt={content?.headerImage.alt}
                    fill
                    objectFit="cover"
                    priority
                    fetchPriority="high"
                  />
                </div>
              )}
              <Flex columnGap="120px">
                <Flex
                  flex={2}
                  direction="column"
                  w={{ base: "100%", md: "auto" }}
                  backgroundColor="#fff"
                  borderTopRadius="20px"
                  px="16px"
                  pt="16px"
                  className="z-10"
                  mt="-100px"
                >
                  <Heading
                    as="h1"
                    textStyle="h1"
                    mb="15px"
                    display={{ base: "block", md: "block" }}
                    itemProp="headline"
                  >
                    {content.title}
                  </Heading>
                  <Text display={{ base: "none", md: "block" }}>{`${dayjs(
                    story.content?.legacy_published_at !== ""
                      ? story.content?.legacy_published_at
                      : story.first_published_at,
                  ).format("MMM D, YYYY")}`}</Text>
                  {content.headerImage && (
                    <div
                      itemProp="image"
                      className="hidden md:block w-full h-[500px] relative"
                    >
                      <Image
                        src={optimisedHeader}
                        alt={content?.headerImage.alt}
                        fill
                        objectFit="contain"
                        objectPosition="50% 50%"
                        priority
                        fetchPriority="high"
                      />
                    </div>
                  )}
                  <Flex
                    mt={{ base: 0, md: "20px" }}
                    mb="20px"
                    columnGap="10px"
                    rowGap="10px"
                    direction={{ base: "column", md: "row" }}
                    align={{ base: "flex-start", md: "center" }}
                  >
                    {content?.authors?.map(
                      ({ content: author, slug }, index) => {
                        const authorName = `${author.firstName} ${author.lastName}`;
                        return (
                          <Link
                            href={`/author/${slug}`}
                            prefetch={false}
                            key={`author_short_${index}`}
                            style={{ textDecoration: "none" }}
                          >
                            <Flex
                              cursor="pointer"
                              direction={{ base: "column", md: "row" }}
                              rowGap="10px"
                              columnGap="10px"
                              itemProp="author"
                              itemScope
                              itemType="https://schema.org/Person"
                            >
                              <HStack
                                p="5px"
                                pr="15px"
                                borderRadius="40px"
                                borderWidth="1px"
                                borderColor="brand.300"
                              >
                                <Image
                                  className="rounded-full h-[30px] w-[30px]"
                                  width={30}
                                  height={30}
                                  src={author?.image?.filename}
                                  alt={authorName}
                                  itemProp="image"
                                />
                                <Text itemProp="name" textStyle="h5bold">
                                  {authorName}
                                </Text>
                              </HStack>
                            </Flex>
                          </Link>
                        );
                      },
                    )}

                    <Text
                      itemProp="datePublished"
                      display={{ base: "block", md: "none" }}
                    >{`${dayjs(
                      story.content?.legacy_published_at !== ""
                        ? story.content?.legacy_published_at
                        : story.first_published_at,
                    ).format("MMM D, YYYY")}`}</Text>

                    <Flex
                      flex={1}
                      direction="row"
                      justify={{ base: "space-between", md: "flex-end" }}
                      alignSelf="stretch"
                    >
                      <Flex
                        flex={1}
                        justify={{ base: "flex-start", md: "flex-end" }}
                        align="center"
                        columnGap="10px"
                      >
                        <TwitterShareButton
                          id="article-social-share-x"
                          title={content.title}
                          url={`https://www.welldoing.org${router.asPath}`}
                        >
                          <XIcon size={32} round={true} />
                        </TwitterShareButton>
                        <FacebookShareButton
                          id="article-social-share-facebook"
                          title={content.title}
                          url={`https://www.welldoing.org${router.asPath}`}
                        >
                          <FacebookIcon size={32} round={true} />
                        </FacebookShareButton>
                        <LinkedinShareButton
                          id="article-social-share-linkedin"
                          title={content.title}
                          url={`https://www.welldoing.org${router.asPath}`}
                        >
                          <LinkedinIcon size={32} round={true} />
                        </LinkedinShareButton>
                        <EmailShareButton
                          id="article-social-share-email"
                          subject={content.title}
                          url={`https://www.welldoing.org${router.asPath}`}
                        >
                          <EmailIcon size={32} round={true} />
                        </EmailShareButton>
                      </Flex>
                      <Flex ml="30px" align="center">
                        <Flex
                          borderRadius="100%"
                          borderColor="brand.400"
                          borderWidth="1px"
                          w="30px"
                          h="30px"
                          minW="30px"
                          justify="center"
                          align="center"
                          cursor="pointer"
                          onClick={handleArticleBookmarkClick}
                        >
                          {savedArticles.includes(story.uuid) ? (
                            <IconBookmarkFilled color="#587500" />
                          ) : (
                            <IconBookmark color="#587500" />
                          )}
                        </Flex>
                      </Flex>
                    </Flex>
                  </Flex>
                  {content.summary?.length > 0 && (
                    <Box
                      borderRadius="20px"
                      padding={{ base: "10px 10px", md: "30px 20px" }}
                      bg="brand.100"
                      mb="40px"
                    >
                      <UnorderedList
                        ml={{ base: 0 }}
                        spacing="10px"
                        textStyle="articleSummary"
                      >
                        {content.summary.map((summary, index) => (
                          <StoryblokComponent
                            key={`summary_${index}`}
                            blok={summary}
                          />
                        ))}
                      </UnorderedList>
                    </Box>
                  )}
                  <VStack itemProp="articleBody" spacing="15px">
                    <TextBlok text={content.body} />
                  </VStack>
                  <Divider mt="40px" borderWidth="1px" />
                  <Flex
                    mt="40px"
                    direction={{ base: "column", md: "row" }}
                    align={{ base: "flex-start", md: "center" }}
                    rowGap="10px"
                  >
                    <Flex direction="column" justify="space-between" flex={1}>
                      <Flex align="center" columnGap="5px">
                        <IconTag size="25px" />
                        <Text textStyle="h3">Article tags</Text>
                      </Flex>
                      <Flex flexWrap="wrap">
                        {story.tag_list.map((tag, index) => (
                          <Link
                            key={`tag_${index}`}
                            href={`/article/tags/${tag}`}
                            itemProp="keywords"
                          >
                            <Button
                              variant="outline"
                              color="brand.400"
                              borderColor="brand.400"
                            >
                              <Text fontWeight="bold">
                                {tagMap[tag] ?? tag}
                              </Text>
                            </Button>
                          </Link>
                        ))}
                      </Flex>
                    </Flex>
                  </Flex>
                  <Flex mt="50px" width="100%" direction="column" rowGap="20px">
                    {content.authors.map((author) => {
                      return (
                        <ArticleAuthorBlurb
                          key={author.uuid}
                          image={author?.content?.image?.filename}
                          firstName={author?.content.firstName}
                          lastName={author?.content.lastName}
                          middleNames={author?.content.middleNames}
                          title={author?.content?.subtitle}
                          summary={author?.content?.description}
                          therapistId={author?.content?.userId}
                          onShowLogin={() => setShowLoginModal(true)}
                          onOpenRequest={setSelectedPractitioner}
                        />
                      );
                    })}
                  </Flex>
                  {content.readFurther && content?.readFurther?.length > 0 && (
                    <div>
                      <Text textStyle="h3" mt="50px" mb="20px">
                        Read further
                      </Text>
                      <Divider borderWidth="1px" />
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "row",
                          justifyContent: "space-between",
                          flexWrap: "wrap",
                          marginTop: "20px",
                          gap: "20px",
                        }}
                      >
                        {content.readFurther.map((article) => (
                          <ArticlePreviewBox
                            author={article?.content?.authors[0].name}
                            imageAlt={article?.content?.headerImage?.alt}
                            title={article?.content?.title}
                            image={article?.content?.headerImage?.filename}
                            slug={article?.slug}
                            key={article?.uuid}
                            width={isSmallScreen ? "100%" : "48%"}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </Flex>
                <ArticleSidebar
                  showReview={false}
                  overrideVideoId={content.overrideSidebarVideoId || null}
                  context={story.tag_list}
                />
              </Flex>
            </Box>
          </Box>
        </Box>
        <style jsx global>{`
          ul p {
            font-weight: inherit;
          }
        `}</style>
      </Container>
    </>
  );
}
